import { showUserAlert } from "../../utils/userErrorMessage";
import React, { useState, useEffect } from "react";
import { createReservationBatch } from "../../services/reservationWrites";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import TimeSelect from "../../components/ui/time-select";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";

const WEEK_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const RESERVATION_YEAR_OPTIONS = Array.from(
  { length: 15 },
  (_, index) => new Date().getFullYear() - 10 + index
);

export default function GeneralForm({ prefill, targetTable = "reservations", reservationMode = "예약" }) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const location = useLocation();

  const initialFormState = {
    year: tomorrow.getFullYear(),
    month: tomorrow.getMonth() + 1,
    day: tomorrow.getDate(),
    buses: 1,
    nights: 0,
    round: "왕복",
    time: "",
    arrival_time: "",
    round_trip_time: "",
    start: "",
    transit: "",
    destination: "",
    user: "",
    user_phone: "",
    naver_map_url: "",
    money: "",
    driver_money: "",
    note: "",
    seat: "대형",
    seat_detail: "",  
  };

  const [form, setForm] = useState(initialFormState);

  // 🔹 휴무 요일(박수 2 이상일 때만 노출): 0(일)~6(토)
  const [offDays, setOffDays] = useState({
    0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false,
  });

  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf) return;

    const d = pf.date ? new Date(pf.date) : null;
    setForm((prev) => ({
      ...prev,
      // 날짜 → 년/월/일
      year: d ? d.getFullYear() : prev.year,
      month: d ? (d.getMonth() + 1) : prev.month,
      day: d ? d.getDate() : prev.day,

      // 공통 필드
      time: pf.time ?? prev.time,
      arrival_time: pf.arrival_time ?? prev.arrival_time,
      round_trip_time: pf.round_trip_time ?? prev.round_trip_time,
      start: pf.start ?? prev.start,
      transit: pf.transit ?? prev.transit,
      destination: pf.destination ?? prev.destination,
      user: pf.user ?? prev.user,
      user_phone: pf.user_phone ?? prev.user_phone,
      naver_map_url: pf.naver_map_url ?? prev.naver_map_url,
      money: pf.money ?? prev.money,
      driver_money: pf.driver_money ?? prev.driver_money,
      note: pf.note ?? prev.note,
      seat: pf.seat ?? prev.seat,
      seat_detail: pf.seat_detail ?? prev.seat_detail,
      round: pf.round ?? prev.round,

      // 일반/학단/투어: 여러 대/박 지원
      buses: pf.buses ?? prev.buses,
      nights: pf.nights ?? prev.nights,

      // 통근/지정차 있을 때만 영향
      car_number: pf.car_number ?? prev.car_number,
      
    }));
  }, [location.state]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]:
        field === "buses" || field === "nights"
          ? String(value).replace(/[^0-9]/g, "") // 숫자만
          : value,
    }));
  };

  const handleToggleOffDay = (dow) => {
    setOffDays((prev) => ({ ...prev, [dow]: !prev[dow] }));
  };

  const handleSubmit = async () => {
    const companyId = localStorage.getItem("email");
    const companyName = localStorage.getItem("companyName") || companyId;
    const baseDate = new Date(
      `${form.year}-${String(form.month).padStart(2, "0")}-${String(form.day).padStart(2, "0")}`
    );
    const groupId = uuidv4();

    const nights = parseInt(form.nights || "0", 10);
    const buses = parseInt(form.buses || "1", 10);

    const endDate = new Date(baseDate);
    endDate.setDate(endDate.getDate() + nights);

    const formatDate = (date) => date.toISOString().split("T")[0];

    const reservations = [];
    let moneyAppliedToFirstServiceDate = false;

    for (let d = 0; d <= nights; d++) {
      const newDate = new Date(baseDate);
      newDate.setDate(baseDate.getDate() + d);

      // 🔸 휴무 요일이면 스킵
      const dow = newDate.getDay(); // 0=일 ~ 6=토
      if (offDays[dow]) continue;

      const formattedDate = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-${String(newDate.getDate()).padStart(2, "0")}`;
      const shouldApplyMoney = !moneyAppliedToFirstServiceDate;

      for (let i = 0; i < buses; i++) {
        reservations.push({
          date: formattedDate,
          time: form.time ? form.time : null,
          arrival_time: form.arrival_time || null,
          round_trip_time: form.round === "왕복" ? form.round_trip_time || null : null,
          start: form.start,
          transit: form.transit,
          destination: form.destination,
          user: form.user,
          user_phone: form.user_phone,
          naver_map_url: form.naver_map_url || null,
          money: shouldApplyMoney ? parseInt(form.money) || 0 : 0,
          driver_money: parseInt(form.driver_money) || 0,
          memo: form.note,
          one_round: form.round,
          number_of_car: 1,
          car_number: "",
          human_name: "",
          phone: "",
          reservation_type: "일반",
          seat: form.seat,
          seat_detail: form.seat_detail || null, 
          days: nights,
          company: companyId,
          company_name: companyName,
          reservation_group_id: groupId,
        });
      }
      moneyAppliedToFirstServiceDate = true;
    }

    // 예약이 하나도 없으면 안내
    if (reservations.length === 0) {
      showUserAlert("휴무 요일 설정으로 생성할 예약이 없습니다.");
      return;
    }

    const summaryData = {
      group_id: groupId,
      start_date: formatDate(baseDate),
      end_date: formatDate(endDate),
      days: nights,
      time: form.time ? form.time : null,
      arrival_time: form.arrival_time || null,
      round_trip_time: form.round === "왕복" ? form.round_trip_time || null : null,
      number_of_car: buses,
      user: form.user,
      user_phone: form.user_phone,
      naver_map_url: form.naver_map_url || null,
      reservation_type: "일반",
      car_type: form.seat,
      start: form.start,
      destination: form.destination,
      transit: form.transit,
      money: parseInt(form.money) || 0,
      driver_money: parseInt(form.driver_money) || 0,
      company: companyId,
      company_name: companyName,
      // 필요하다면 offDays를 JSON으로 보관(선택)
      // off_days: offDays
    };

    const { error: error2 } = await createReservationBatch(reservations, [summaryData], targetTable);
    if (error2) {
      showUserAlert("등록 실패: " + error2.message);
      return;
    }

    showUserAlert(`일반 ${reservationMode}이 등록되었습니다.`);
    setForm(initialFormState);
    // 휴무 요일 초기화
    setOffDays({ 0:false,1:false,2:false,3:false,4:false,5:false,6:false });
  };

  return (
    <div className="px-4 pt-1 pb-4 space-y-2">
      <h2 className="text-lg font-semibold">일반 {reservationMode} 등록</h2>
      <Card className="p-2">
        <CardContent className="bg-white">
          <div className="reservation-form-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="reservation-section-title">운행 정보</div>
            {/* 운행일 */}
            <div>
              <label className="block font-semibold mb-1">운행시작일</label>
              <div className="flex gap-2 items-center">
                <select value={form.year} onChange={(e) => handleChange("year", e.target.value)}>
                  {RESERVATION_YEAR_OPTIONS.map((y) => (
                    <option key={y}>{y}</option>
                  ))}
                </select>
                <select value={form.month} onChange={(e) => handleChange("month", e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
                <select value={form.day} onChange={(e) => handleChange("day", e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 차량 크기 / 인승 */}
            <div>
              <label className="block font-semibold mb-1">차량 크기/인승</label>
              <div className="flex gap-2">
                <select className="w-24" value={form.seat} onChange={(e) => handleChange("seat", e.target.value)}>
                  {["대형", "중형", "우등"].map((seat) => (
                    <option key={seat} value={seat}>
                      {seat}
                    </option>
                  ))}
                </select>
                <Input
                  type="text"
                  placeholder="예) 45인승"
                  value={form.seat_detail}
                  onChange={(e) => handleChange("seat_detail", e.target.value)}
                />
              </div>
            </div>

            {/* 버스 / 박 */}
            <div>
              <label className="block font-semibold mb-1">버스 / 박</label>
              <div className="flex gap-2 items-center">
                <label>차량대수:</label>
                <Input
                  type="number"
                  className="w-20"
                  value={form.buses}
                  onChange={(e) => handleChange("buses", e.target.value)}
                />
                <label>박:</label>
                <Input
                  type="number"
                  className="w-20"
                  value={form.nights}
                  onChange={(e) => handleChange("nights", e.target.value)}
                />
              </div>

              {/* 🔹 박수가 2 이상일 때만 노출되는 휴무 요일 선택 */}
              {parseInt(form.nights || "0", 10) >= 2 && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold">휴무 요일:</span>
                  {WEEK_LABELS.map((label, dow) => (
                    <label key={dow} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={!!offDays[dow]}
                        onChange={() => handleToggleOffDay(dow)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 출발시간 + 왕복 */}
            <div>
              <label className="block font-semibold mb-1">출발시간</label>
              <div className="flex gap-2 items-center">
                <select value={form.round} onChange={(e) => handleChange("round", e.target.value)}>
                  <option>왕복</option>
                  <option>편도</option>
                </select>
                <TimeSelect value={form.time} onChange={(value) => handleChange("time", value)} />
              </div>
            </div>
            <div>
              <label className="block font-semibold mb-1">도착 예상 시간</label>
              <TimeSelect value={form.arrival_time} onChange={(value) => handleChange("arrival_time", value)} />
            </div>
            {form.round === "왕복" && (
              <div>
                <label className="block font-semibold mb-1">회차 시간</label>
                <TimeSelect value={form.round_trip_time} onChange={(value) => handleChange("round_trip_time", value)} />
              </div>
            )}

            <div className="reservation-section-title">경로 정보</div>
            {/* 출발지 / 경유지 / 행선지 / 사용자/노선명 */}
            <div>
              <label className="block font-semibold mb-1">출발지</label>
              <Input type="text" placeholder="출발지" value={form.start} onChange={(e) => handleChange("start", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">경유지</label>
              <Input type="text" placeholder="경유지" value={form.transit} onChange={(e) => handleChange("transit", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">행선지</label>
              <Input type="text" placeholder="행선지" value={form.destination} onChange={(e) => handleChange("destination", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">사용자/노선명</label>
              <Input type="text" placeholder="사용자/노선명" value={form.user} onChange={(e) => handleChange("user", e.target.value)} />
            </div>

            {/* 연락처 / 금액 */}
            <div>
              <label className="block font-semibold mb-1">사용자 전화번호</label>
              <Input type="text" placeholder="전화번호" value={form.user_phone} onChange={(e) => handleChange("user_phone", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">네이버 지도 링크</label>
              <Input type="url" placeholder="https://map.naver.com/..." value={form.naver_map_url} onChange={(e) => handleChange("naver_map_url", e.target.value)} />
            </div>

            <div className="reservation-section-title">금액 정보</div>
            <div>
              <label className="block font-semibold mb-1">받을 금액</label>
              <Input type="number" placeholder="받을 금액" value={form.money} onChange={(e) => handleChange("money", e.target.value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">기사 금액</label>
              <Input type="number" placeholder="기사 금액" value={form.driver_money} onChange={(e) => handleChange("driver_money", e.target.value)} />
            </div>

            <div className="reservation-section-title">기타</div>
            {/* 메모 */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block font-semibold mb-1">기타</label>
              <Input type="text" placeholder="기타" value={form.note} onChange={(e) => handleChange("note", e.target.value)} />
            </div>

            {/* 등록 버튼 */}
            <div className="reservation-action-row">
              <Button onClick={handleSubmit}>등록</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
